// Plain-English nutrition & fitness guide for new athletes.
// need: "Essential" | "Helpful" | "Optional"
export const LEARN_CATEGORIES = [
  "Basics", "Protein", "Carbs & Fuelling", "Everyday Fuelling", "Hydration",
  "Supplements", "Recovery", "Race Day", "Female Athlete", "Weight Management",
  "Timing & Gut", "Plant-Based", "Meal Prep", "Using the App",
];

export const LEARN_ITEMS = [
  {
    id: "calories", category: "Basics", term: "Calories (energy)", need: "Essential",
    simple: "Think of calories as petrol in your tank — training burns it, food refills it.",
    what: "A calorie is a unit of energy. Your body needs a baseline just to function, plus extra to fuel training.",
    why: "Eat too little and you'll feel flat, recover poorly and lose muscle. Too much and you gain unwanted weight. Endurance athletes usually need MORE on hard days and less on rest days — that's exactly what your plan does.",
  },
  {
    id: "macros", category: "Basics", term: "Macros (protein, carbs, fat)", need: "Essential",
    simple: "The three big building blocks of food. Carbs = fuel, protein = repair, fat = long-burn energy & hormones.",
    what: "Macronutrients are the nutrients you need in large amounts. Each gram of protein and carbs gives ~4 calories; fat gives ~9.",
    why: "Balancing them matters more than obsessing over any single one. Your plan sets daily targets for each so you fuel, repair and stay healthy.",
  },
  {
    id: "protein-types", category: "Protein", term: "Types of protein", need: "Essential",
    simple: "Different proteins digest at different speeds — all of them help you repair and build.",
    what: "Whey (fast, from milk) is great post-workout. Casein (slow, from milk) suits before bed. Plant proteins (pea, soy, rice) work well too — blends cover all the amino acids. Whole foods (chicken, fish, eggs, dairy, tofu, beans) are the foundation.",
    why: "You mainly need ENOUGH total protein across the day. Powders are just a convenient top-up, not a requirement, if your meals already hit target.",
  },
  {
    id: "how-much-protein", category: "Protein", term: "How much protein do I need?", need: "Essential",
    simple: "Roughly a palm-sized portion at each meal for most active people.",
    what: "Endurance and strength athletes typically aim for ~1.6–2.2 g of protein per kg of bodyweight per day, spread across meals.",
    why: "Spreading it out (rather than one big hit) helps muscle repair through the day. Your plan already portions this for you.",
  },
  {
    id: "carbs", category: "Carbs & Fuelling", term: "Carbohydrates", need: "Essential",
    simple: "Your main fuel for anything fast or long. Low fuel = the dreaded 'bonk'.",
    what: "Carbs are stored as glycogen in muscles and liver. Bigger/harder sessions burn more, so you need more carbs on those days.",
    why: "Under-fuelling carbs is the #1 reason sessions feel awful. This is why your plan scales carbs up on your key training days.",
  },
  {
    id: "gels", category: "Carbs & Fuelling", term: "Energy gels & during-session fuel", need: "Helpful",
    simple: "Fast carbs you take mid-session so you don't run out of fuel.",
    what: "Gels, chews, drinks and bananas deliver quick carbs during exercise. A common guide is ~30–60 g carbs per hour for efforts over ~75–90 minutes (more for racing, if your gut tolerates it).",
    why: "For short easy sessions you rarely need them. For long rides/runs and races they can be the difference between finishing strong and fading. Practise in training first.",
  },
  {
    id: "electrolytes", category: "Hydration", term: "Electrolytes & sodium", need: "Helpful",
    simple: "The salts you lose in sweat. They help you hold onto fluid and avoid cramping.",
    what: "Sodium, potassium, magnesium and chloride. Sodium is the big one for athletes because you sweat a lot of it out, especially in heat or long sessions.",
    why: "Water alone on very long/hot sessions can leave you flat or cramping. Electrolyte tabs/drinks help. For short sessions, normal food usually covers it.",
  },
  {
    id: "hydration", category: "Hydration", term: "How much should I drink?", need: "Essential",
    simple: "Sip regularly; use the colour of your pee as a rough gauge (pale straw = good).",
    what: "Needs vary with body size, heat and sweat rate. Your plan sets a sensible daily baseline and adds fluid guidance around training.",
    why: "Even mild dehydration hurts performance and focus. You don't need to over-drink — steady sipping across the day beats gulping litres at once.",
  },
  {
    id: "creatine", category: "Supplements", term: "Creatine", need: "Optional",
    simple: "One of the most researched, safe supplements — helps with power, strength and recovery.",
    what: "Creatine monohydrate (~3–5 g daily) helps your muscles produce short bursts of energy. It's found in small amounts in meat/fish. No need to 'load' — daily use works.",
    why: "Great value and well-proven for strength/power and repeated efforts, with modest endurance benefits. Optional, not essential — plenty of athletes thrive without it.",
  },
  {
    id: "caffeine", category: "Supplements", term: "Caffeine", need: "Optional",
    simple: "A legal performance booster in coffee, tea and some gels — makes hard efforts feel easier.",
    what: "Caffeine can improve endurance and perceived effort. Common dose is ~3 mg per kg bodyweight ~45–60 min before a session.",
    why: "Effective but individual — some people get jittery or wrecked sleep. Avoid late in the day and don't try a big dose for the first time on race day.",
  },
  {
    id: "supplements-truth", category: "Supplements", term: "Do I NEED supplements?", need: "Optional",
    simple: "Mostly no. Food first — supplements fill gaps, they don't replace good eating.",
    what: "For most athletes, a solid diet covers the essentials. The few with decent evidence: creatine, caffeine, and electrolytes for long/hot sessions. Vitamin D or iron only if a test/GP says so.",
    why: "The supplement aisle is overwhelming on purpose. Don't feel you must buy things — nail your meals, sleep and hydration first. That's 90% of the win.",
  },
  {
    id: "recovery", category: "Recovery", term: "Recovery, sleep & rest days", need: "Essential",
    simple: "You get fitter when you REST, not just when you train. Sleep is the ultimate supplement.",
    what: "Adaptation happens between sessions. Sleep (7–9 h), easy days and enough food are what let hard training actually make you fitter.",
    why: "Skipping recovery and under-eating leads to fatigue, illness and stalled progress. Rest days in your plan are doing real work — trust them.",
  },
  {
    id: "carb-loading", category: "Race Day", term: "Carb-loading before a race", need: "Helpful",
    simple: "Top up your fuel tank in the 1–3 days before a big endurance event.",
    what: "For events over ~90 min, raising carbs to ~8–10 g/kg/day for 1–3 days maximises muscle glycogen. Ease off training volume at the same time.",
    why: "A full tank delays fatigue and the 'bonk'. Your plan bumps carbs on the days before a flagged race so this happens automatically.",
  },
  {
    id: "race-breakfast", category: "Race Day", term: "The race-morning meal", need: "Essential",
    simple: "Eat familiar, easy-to-digest carbs 2–3 hours before the start.",
    what: "Aim for ~1–3 g/kg of carbs (porridge, toast & honey, banana, bagel) with a little protein, low fat and low fibre. Sip fluids with electrolytes.",
    why: "Tops up liver glycogen after the overnight fast without upsetting your gut. Never try something new on race day — rehearse it in training.",
  },
  {
    id: "deficit-safely", category: "Weight Management", term: "Losing weight without losing performance", need: "Helpful",
    simple: "Go slow, keep protein high, and don't cut carbs around hard sessions.",
    what: "A modest deficit (~10–20% below maintenance), high protein (2.0+ g/kg) and fuelling your key sessions preserves muscle and training quality. Aim for ~0.5% bodyweight/week.",
    why: "Crash diets tank your training, hormones and recovery. Your plan's fat-loss goal builds the deficit on easier days and protects your big days.",
  },
  {
    id: "body-comp", category: "Weight Management", term: "Weight vs body composition", need: "Optional",
    simple: "The scale is one noisy data point — trends and how you perform matter more.",
    what: "Daily weight swings with food, fluid and glycogen. Track a 7-day average and pair it with performance, energy and how clothes fit.",
    why: "Chasing a daily number leads to under-fuelling. The Tracker's bodyweight trend smooths the noise so you see the real direction.",
  },
  {
    id: "nutrient-timing", category: "Timing & Gut", term: "Does meal timing matter?", need: "Helpful",
    simple: "Total daily intake matters most, but timing carbs around training helps.",
    what: "Carbs before/around key sessions fuel them; protein spread every 3–4 h aids repair. The old '30-min anabolic window' is much wider than once thought.",
    why: "You don't need to panic-eat the second you finish. Just don't skip refuelling after a hard/long session, especially if you train again soon.",
  },
  {
    id: "gut-training", category: "Timing & Gut", term: "Training your gut", need: "Helpful",
    simple: "Your stomach can be trained to handle more fuel during exercise.",
    what: "Practising in-session fuelling (gels, drinks, real food) gradually raises how many carbs/hour you tolerate without GI distress — up to 90 g/h for some.",
    why: "Gut problems ruin more races than fitness does. Rehearse race fuelling in training so nothing surprises you on the day.",
  },
  {
    id: "plant-protein", category: "Plant-Based", term: "Getting enough protein plant-based", need: "Essential",
    simple: "Totally doable — lean on soy, legumes, grains and a little planning.",
    what: "Combine sources (beans + rice, tofu, tempeh, edamame, seitan, lentils, soy milk, pea/soy protein) to cover all amino acids across the day. Aim slightly higher total (~1.8–2.2 g/kg).",
    why: "Plant proteins are a touch less concentrated, so variety and volume matter. Your vegan/vegetarian pattern already picks meals that stack up.",
  },
  {
    id: "plant-iron-b12", category: "Plant-Based", term: "Iron, B12 & the nutrients to watch", need: "Helpful",
    simple: "Plant-based athletes should keep an eye on B12, iron, omega-3 and vitamin D.",
    what: "Supplement B12. Pair iron-rich plants (lentils, tofu, spinach) with vitamin C to absorb more. Consider algae omega-3. Test iron/vitamin D if you feel flat.",
    why: "These are the common gaps on a plant-based diet and they directly affect energy and endurance. Easy to cover once you know to look.",
  },
  {
    id: "batch-cooking", category: "Meal Prep", term: "Batch cooking & meal prep", need: "Helpful",
    simple: "Cook once, eat 3–4 times. The secret weapon of consistent athletes.",
    what: "Prep staple carbs (rice, potatoes, oats), proteins (chicken, tofu, mince, eggs) and easy veg in bulk, then assemble. The plan's 'batch' cooking style reuses meals across the week.",
    why: "When food is ready to grab, you actually hit your targets on busy days instead of grabbing whatever's convenient.",
  },
  {
    id: "budget-eating", category: "Meal Prep", term: "Fuelling on a budget", need: "Optional",
    simple: "Oats, eggs, tinned fish, frozen veg, legumes and own-brand basics go a long way.",
    what: "Cheap performance staples: rolled oats, rice, pasta, potatoes, eggs, tinned tuna/mackerel, frozen veg & berries, lentils, own-brand yogurt and milk.",
    why: "Good fuelling doesn't need fancy products. Nail the cheap basics and put the savings toward the few supplements that are actually worth it.",
  },
  {
    id: "app-morning-report", category: "Using the App", term: "Morning Report (daily readiness)", need: "Essential",
    simple: "The 60-second wake-up check-in that scores how ready you are to train today.",
    what: "In the Tracker's Morning Report section, rate sleep, energy, mood, soreness and stress out of 5, and log sleep hours & steps. We turn it into a 0–100 readiness score.",
    why: "It flags when you're under-recovered before a hard day so you can adjust fuel or intensity. Trends show up in Insights across 7d / 30d / 90d / 1yr / 2yr / All-time.",
  },
  {
    id: "app-meal-planning-toggle", category: "Using the App", term: "Meal Planning toggle (macro-only mode)", need: "Helpful",
    simple: "Turn meal planning off in Step 2 if you just want the daily numbers, not the meals.",
    what: "In Step 2 · Dietary Requirements, the Meal Planning Yes/No toggle decides whether Steps 6 (Meal Pathways) and 7 (Shopping List) run. Off = macro-only; you get calories, macros, hydration and training-day scaling without pre-picked meals.",
    why: "Some athletes already know how they'll eat and only want the targets. This keeps the plan lean and cuts wizard time in half.",
  },
  {
    id: "app-insights", category: "Using the App", term: "Insights dashboard", need: "Helpful",
    simple: "Your long-term trends: weight, adherence, training load, readiness and food quality.",
    what: "Insights shows headline cards (weight, adherence, RPE, readiness, sleep, steps) plus Food Trend cards — 5-a-day hits, protein sources, processed-food ratio and meat vs plant. Switch between 7d, 30d, 90d, 1 year, 2 years or All-time.",
    why: "Day-to-day numbers are noisy; trends over weeks and months are what actually tell you if you're getting fitter, leaner or under-fuelling.",
  },
  {
    id: "app-tracker-sections", category: "Using the App", term: "How the Tracker is laid out", need: "Essential",
    simple: "Each area of the Tracker is a clean tap-to-open dropdown, so nothing is buried.",
    what: "Meals · Vitamins & Minerals · Hydration · Bodyweight · Sessions · Morning Report · Notes — tap a header to open, tap again to close. Everything auto-saves.",
    why: "You only see what you're working on, and daily targets always match the plan for that day (including sessions imported from TrainingPeaks).",
  },
  {
    id: "app-trainingpeaks-sync", category: "Using the App", term: "TrainingPeaks Calendar Sync", need: "Helpful",
    simple: "Auto-pull your scheduled TP sessions so fuelling scales with the day's training.",
    what: "In Step 4 · Training Week, paste your TrainingPeaks iCal / webcal URL to pull in the week's sessions. Requires a TrainingPeaks Premium account. You can add TP Premium (£9.99/mo) at checkout too.",
    why: "You never have to re-enter sessions or manually bump carbs on hard days — the plan does it for you.",
  },
  {
    id: "app-sunday-checkin", category: "Using the App", term: "Sunday check-in", need: "Helpful",
    simple: "A 60-second weekly reflection every Sunday that steers next week's fuelling.",
    what: "Rate overall feeling, motivation, fatigue, hydration, appetite, mood, sleep and soreness for the past week. You'll see a week-on-week trend and score band.",
    why: "Zoom-out perspective matters. It catches slow drifts (creeping fatigue, low motivation) that daily numbers can miss.",
  },

  // ---------------------------------------------------------------------------
  // Extended knowledge base (June 2026) — race week, everyday fuelling,
  // female-athlete specifics, supplement truth table, gut/hydration and
  // body-composition done properly.
  // ---------------------------------------------------------------------------
  {
    id: "race-week-taper-fuel", category: "Race Day", term: "Race week: eat more, train less", need: "Essential",
    simple: "Training drops but carbs go UP. That's not indulgence — it's the plan.",
    what: "In the final 3 days you taper volume while lifting carbs from ~5 g/kg toward 8–10 g/kg on the day before. The Race Hub times this for you (T-3 → T-0).",
    why: "Topped-off glycogen is worth minutes over a long race. Athletes who cut food because they're 'not training' arrive flat and heavy-legged.",
  },
  {
    id: "in-race-carbs", category: "Race Day", term: "Carbs per hour during a race", need: "Essential",
    simple: "Under 75 min: little needed. 75–150 min: 30–60 g/hr. 2.5h+: 60–90 g/hr. Ultra: up to 120 g/hr.",
    what: "Above ~60 g/hr you need multiple transportable carbs (glucose + fructose, roughly 2:1) because glucose transporters saturate.",
    why: "It's the single biggest lever on late-race pace. The Race Hub calculates your per-hour and race-total target from your session duration.",
  },
  {
    id: "race-day-toilet-gut", category: "Race Day", term: "Race-morning gut plan", need: "Helpful",
    simple: "Low fibre, low fat, familiar food, finished ~3 hours before the gun.",
    what: "White bread/rice/porridge with honey or jam, plus 500–750 ml fluid with sodium 90 minutes out and a small top-up 15 minutes before.",
    why: "Fibre and fat sit in the gut and cause the classic mid-race stitch or emergency stop. Nothing new on race day — ever.",
  },
  {
    id: "post-race-recovery", category: "Race Day", term: "The first hour after the line", need: "Essential",
    simple: "~1 g/kg carbs plus 20–40 g protein, then rehydrate to 150 % of the weight you lost.",
    what: "Glycogen resynthesis is fastest in the first 60–90 minutes. Chocolate milk, a smoothie, rice and chicken all work — whatever you'll actually swallow.",
    why: "It sets up the next 48 hours. Skip it and you'll feel wrecked for days, especially if you race again soon.",
  },
  {
    id: "multi-day-racing", category: "Race Day", term: "Stage races & back-to-back days", need: "Optional",
    simple: "Refuel like it's a job: carbs within 30 min, a real meal within 2 hours, repeat.",
    what: "Aim 8–10 g/kg carbs across each recovery day, protein 2.0 g/kg, plus sodium-rich fluids. Little-and-often beats two huge meals.",
    why: "Day 3 is where under-fuelled athletes fall apart. Consistency between stages decides your finish more than the effort itself.",
  },
  {
    id: "everyday-breakfast", category: "Everyday Fuelling", term: "Do I have to eat before training?", need: "Helpful",
    simple: "Short and easy: optional. Hard, long or race-pace: always eat something.",
    what: "For quality sessions take 1–2 g/kg carbs 1–3 hours before. If you're short on time, 30 g of fast carbs 20–30 min before still helps.",
    why: "Fasted quality work costs you intensity and raises injury and illness risk. Fasted easy work is a legitimate (but optional) tool.",
  },
  {
    id: "everyday-post-session", category: "Everyday Fuelling", term: "What to eat after a session", need: "Essential",
    simple: "Carbs to refill, protein to repair, fluid to rehydrate — within a couple of hours.",
    what: "A practical target is 1 g/kg carbs plus 0.3 g/kg protein. If your next session is within 8 hours, do it sooner and bigger.",
    why: "The tighter your training turnaround, the more the window matters. With 24 hours between sessions, total daily intake matters more than timing.",
  },
  {
    id: "everyday-two-a-day", category: "Everyday Fuelling", term: "Doubles & big training days", need: "Helpful",
    simple: "Treat the gap between sessions as a refuelling job, not a rest.",
    what: "Carb-rich snack immediately after session one, a full meal in between, and hydrate with sodium. Front-load carbs earlier in the day.",
    why: "Session two is where under-fuelling shows up. Your plan already lifts total calories on doubles — the trick is spreading them.",
  },
  {
    id: "everyday-shift-travel", category: "Everyday Fuelling", term: "Shift work, travel & odd hours", need: "Optional",
    simple: "Anchor food to your training, not the clock.",
    what: "Pack portable protein and carbs (bars, shakes, oatcakes, tuna pouches, bagels). Keep a fixed pre-session snack pattern wherever you are.",
    why: "Missed meals on travel/night shifts are the most common cause of a bad week. Planning three portable options removes the decision.",
  },
  {
    id: "everyday-alcohol", category: "Everyday Fuelling", term: "Alcohol & training", need: "Optional",
    simple: "It blunts recovery, muscle repair and sleep quality — and dehydrates you.",
    what: "If you drink, do it after you've eaten and rehydrated, and keep it away from the 48 hours before a race or key session.",
    why: "One heavy night can cost a week of adaptation. Race week is a zero — the Race Hub timeline flags it from T-3.",
  },
  {
    id: "female-cycle", category: "Female Athlete", term: "Fuelling across your cycle", need: "Helpful",
    simple: "Energy needs and cravings genuinely shift — that's physiology, not weakness.",
    what: "In the luteal phase (after ovulation) resting energy use rises slightly, core temperature runs higher and carb use changes — you may need a little more food, more fluid and more sodium. The follicular phase usually feels best for hard quality work.",
    why: "Matching food to the phase reduces the 'I feel awful for no reason' weeks. Log symptoms in the Tracker journal so patterns become obvious.",
  },
  {
    id: "female-reds", category: "Female Athlete", term: "RED-S & low energy availability", need: "Essential",
    simple: "Not eating enough for your training load — the most damaging thing an endurance athlete can do.",
    what: "Warning signs: lost or irregular periods, stress fractures, constant illness, poor sleep, stalled performance, cold hands, low mood. It affects men too (low testosterone, low libido, bone loss).",
    why: "It costs bone density, hormones and years of progress. If you recognise these signs, raise intake and speak to a doctor or sports dietitian — this app is not a substitute for medical care.",
  },
  {
    id: "female-iron", category: "Female Athlete", term: "Iron, ferritin & fatigue", need: "Helpful",
    simple: "Low iron feels exactly like being unfit — flat, breathless, heavy legs.",
    what: "Menstruating endurance athletes are the highest-risk group. Red meat, shellfish, lentils, tofu and fortified cereals all help; vitamin C boosts absorption while tea/coffee with the meal blocks it.",
    why: "Never self-prescribe high-dose iron — get ferritin tested first. Too much iron is harmful.",
  },
  {
    id: "female-bone-health", category: "Female Athlete", term: "Bones, calcium & vitamin D", need: "Helpful",
    simple: "Runners and cyclists both need calcium and vitamin D — cyclists especially.",
    what: "Aim ~1000 mg calcium daily (dairy, fortified plant milks, tofu, sardines) and consider 10 µg (400 IU) vitamin D through UK winter months.",
    why: "Low-impact sport plus under-fuelling is a stress-fracture recipe. Bone loss is slow to build and slow to fix.",
  },
  {
    id: "supp-truth-table", category: "Supplements", term: "Supplement truth table", need: "Optional",
    simple: "Four things are worth your money. Most of the rest is marketing.",
    what: "STRONG evidence: creatine monohydrate (3–5 g/day), caffeine (3–6 mg/kg pre-event), nitrate/beetroot (2–3 h pre, endurance), sodium bicarbonate (high-intensity 1–7 min efforts, GI risk). USEFUL AS FOOD: whey or plant protein, carb drinks/gels, electrolytes, vitamin D in winter. WEAK OR NO EVIDENCE for performance: BCAAs, glutamine, ketone esters, fat burners, testosterone boosters, most greens powders, collagen for performance (may help tendon with vitamin C).",
    why: "Supplements are the last 1 %. Sleep, total calories, carbs and protein are the other 99 % — and they're free.",
  },
  {
    id: "supp-safety", category: "Supplements", term: "Contamination & drug testing", need: "Optional",
    simple: "If you're tested, only use batch-certified products.",
    what: "Look for Informed Sport or Informed Choice certification on the tub. Herbal 'proprietary blends' are the highest risk.",
    why: "Strict liability means a contaminated supplement is still your ban. Certification is cheap insurance.",
  },
  {
    id: "gut-gi-issues", category: "Timing & Gut", term: "Fixing race-day stomach problems", need: "Helpful",
    simple: "Usually it's too concentrated a drink, too much fibre or fat, or dehydration — rarely bad luck.",
    what: "Fixes in order: dilute your drink toward 6–8 % carbs, use glucose+fructose blends above 60 g/hr, cut fibre 24–48 h out, keep fat low pre-race, and drink alongside gels. Train the exact combination you'll race with.",
    why: "GI distress is the #1 reason endurance races go wrong, and it's almost entirely trainable.",
  },
  {
    id: "gut-fodmap", category: "Timing & Gut", term: "Low-residue days before a race", need: "Optional",
    simple: "For 24–48 hours, swap high-fibre foods for white versions.",
    what: "White rice, white bread, peeled potatoes, ripe bananas, tinned peaches. Pause bran, lentils, big salads, brassicas and dried fruit.",
    why: "Less undigested bulk in the gut means less bouncing, less bloating and fewer emergency stops.",
  },
  {
    id: "hydration-sweat-test", category: "Hydration", term: "Work out your own sweat rate", need: "Helpful",
    simple: "Weigh yourself naked before and after a 60-minute session.",
    what: "Each kg lost ≈ 1 litre of sweat. Add back what you drank during the session to get your true hourly rate, then plan 70–90 % replacement in racing.",
    why: "Generic advice is a guess. Heavy sweaters need far more fluid and sodium than the label on a tab suggests.",
  },
  {
    id: "hydration-hyponatraemia", category: "Hydration", term: "Can you drink too much?", need: "Essential",
    simple: "Yes. Over-drinking plain water in long events is dangerous.",
    what: "Hyponatraemia (diluted blood sodium) causes nausea, headache, confusion and swelling. Risk rises in slower finishers who drink at every station.",
    why: "Drink to thirst with sodium rather than to a fixed schedule. Weight GAIN across a long race is the red flag.",
  },
  {
    id: "bodycomp-performance-first", category: "Weight Management", term: "Performance weight vs lowest weight", need: "Essential",
    simple: "The lightest version of you is rarely the fastest version of you.",
    what: "Aim for a small deficit (10–20 % below maintenance, roughly 0.25–0.5 % of body weight per week) only in base phases — never in race build or race week.",
    why: "Aggressive cuts strip muscle, hormones and immunity. Slow and boring keeps the watts.",
  },
  {
    id: "bodycomp-fuel-the-work", category: "Weight Management", term: "Fuel the work required", need: "Helpful",
    simple: "Put the food where the training is: big on hard days, lighter on easy days.",
    what: "Keep protein and total carbs high around key sessions and take the deficit from easy or rest days. This is exactly what your generated plan does day by day.",
    why: "You get the deficit without sabotaging the sessions that actually make you faster.",
  },
  {
    id: "bodycomp-scales", category: "Weight Management", term: "Reading the scales properly", need: "Helpful",
    simple: "Judge the 7-day average, never a single morning.",
    what: "Weigh at the same time, after the toilet, before food. Glycogen, salt and hormones can swing the number 1–2 kg overnight.",
    why: "The Tracker plots your trend line for exactly this reason — one bad reading has ruined many good weeks.",
  },
  {
    id: "bodycomp-plateau", category: "Weight Management", term: "When weight loss stalls", need: "Optional",
    simple: "Usually more food creeping in or less daily movement — not a 'broken metabolism'.",
    what: "Take a 1–2 week maintenance break, log everything honestly for 7 days, protect protein and sleep, then reassess.",
    why: "Diet breaks restore hormones and training quality, and they usually restart progress faster than cutting harder.",
  },
  {
    id: "illness-fuelling", category: "Recovery", term: "Eating when you're ill or injured", need: "Helpful",
    simple: "Don't slash calories — repair costs energy too.",
    what: "Keep protein high (up to 2.0–2.5 g/kg when immobilised), trim some carbs to match the lower training load, and prioritise fruit, veg and fluids.",
    why: "Under-eating during injury accelerates muscle loss and slows healing. Come back fuelled, not shrunken.",
  },
  {
    id: "heat-cold-altitude", category: "Recovery", term: "Heat, cold & altitude", need: "Optional",
    simple: "Heat needs fluid and sodium. Cold hides thirst. Altitude needs carbs and iron.",
    what: "In heat, raise sodium and fluid and pre-cool. In cold, drink on a schedule because thirst is blunted. At altitude, carb use rises and iron demand climbs before a camp.",
    why: "Environment can matter more than the training itself. Plan the nutrition changes before you travel.",
  },

];

// Athlete-friendly recipes for the Nutrition Guide. Macros are approximate per serving.
export const RECIPES = [
  {
    id: "r_overnight_oats", meal: "breakfast", name: "High-Protein Overnight Oats", tags: ["high-carb", "prep-ahead", "veg"],
    kcal: 520, protein: 34, carb: 68, fat: 12,
    ingredients: ["80g rolled oats", "1 scoop (30g) whey or soy protein", "200ml milk of choice", "120g mixed berries", "1 tbsp chia seeds", "Drizzle of honey"],
    method: ["Mix oats, protein, chia and milk in a jar.", "Stir in half the berries and the honey.", "Refrigerate overnight.", "Top with remaining berries in the morning."],
  },
  {
    id: "r_eggs_toast", meal: "breakfast", name: "Eggs, Avocado & Sourdough", tags: ["quick", "veg"],
    kcal: 480, protein: 26, carb: 40, fat: 24,
    ingredients: ["2 slices sourdough", "3 eggs", "1/2 avocado", "Handful spinach", "Chilli flakes, salt & pepper"],
    method: ["Toast the sourdough.", "Scramble or poach the eggs.", "Smash avocado onto toast, season.", "Top with eggs and wilted spinach."],
  },
  {
    id: "r_chicken_rice", meal: "lunch", name: "Chicken, Rice & Roasted Veg Bowl", tags: ["batch-friendly", "high-protein"],
    kcal: 650, protein: 48, carb: 72, fat: 16,
    ingredients: ["150g chicken breast", "100g (dry) rice", "1 mixed pepper", "1 courgette", "1 tbsp olive oil", "Paprika, garlic, lemon"],
    method: ["Cook rice.", "Roast chopped veg in oil and spices at 200°C for 20 min.", "Pan-cook seasoned chicken until done.", "Combine and finish with lemon."],
  },
  {
    id: "r_tuna_pasta", meal: "lunch", name: "Tuna & Sweetcorn Pasta", tags: ["budget", "quick"],
    kcal: 600, protein: 42, carb: 80, fat: 12,
    ingredients: ["100g (dry) pasta", "1 tin tuna", "100g sweetcorn", "2 tbsp light mayo or yogurt", "Handful rocket", "Black pepper"],
    method: ["Boil the pasta, drain.", "Mix tuna, sweetcorn and mayo/yogurt.", "Fold through warm pasta.", "Stir in rocket and season."],
  },
  {
    id: "r_salmon_potato", meal: "dinner", name: "Baked Salmon, Potatoes & Greens", tags: ["omega-3", "high-protein"],
    kcal: 680, protein: 46, carb: 55, fat: 28,
    ingredients: ["150g salmon fillet", "300g new potatoes", "150g tenderstem broccoli", "1 tbsp olive oil", "Lemon, dill, salt"],
    method: ["Boil or roast potatoes.", "Bake salmon with lemon & dill at 190°C for 15 min.", "Steam the broccoli.", "Plate and drizzle with olive oil."],
  },
  {
    id: "r_beef_stirfry", meal: "dinner", name: "Beef & Veg Noodle Stir-Fry", tags: ["quick", "high-protein"],
    kcal: 640, protein: 44, carb: 66, fat: 20,
    ingredients: ["150g lean beef strips", "1 nest egg noodles", "200g stir-fry veg", "1 tbsp soy sauce", "1 tsp sesame oil", "Garlic & ginger"],
    method: ["Cook noodles, set aside.", "Sear beef in a hot wok.", "Add veg, garlic and ginger; stir-fry 3 min.", "Add noodles, soy and sesame oil; toss."],
  },
  {
    id: "r_tofu_curry", meal: "dinner", name: "Tofu & Chickpea Coconut Curry", tags: ["vegan", "batch-friendly"],
    kcal: 620, protein: 30, carb: 74, fat: 22,
    ingredients: ["200g firm tofu", "1 tin chickpeas", "1/2 tin light coconut milk", "1 tin chopped tomatoes", "100g (dry) rice", "Curry paste, spinach"],
    method: ["Cook rice.", "Fry cubed tofu until golden.", "Add paste, tomatoes, coconut milk and chickpeas; simmer 12 min.", "Stir in spinach and serve over rice."],
  },
  {
    id: "r_yogurt_pot", meal: "snack", name: "Greek Yogurt, Berries & Granola", tags: ["quick", "high-protein", "veg"],
    kcal: 300, protein: 22, carb: 34, fat: 8,
    ingredients: ["200g Greek yogurt", "80g berries", "30g granola", "1 tsp honey"],
    method: ["Spoon yogurt into a pot.", "Top with berries and granola.", "Drizzle with honey."],
  },
  {
    id: "r_shake", meal: "snack", name: "Recovery Banana Protein Shake", tags: ["post-workout", "quick"],
    kcal: 320, protein: 30, carb: 42, fat: 4,
    ingredients: ["1 scoop whey or plant protein", "1 banana", "300ml milk of choice", "Handful ice", "Cinnamon"],
    method: ["Add everything to a blender.", "Blend until smooth.", "Best within 60 min of a hard session."],
  },
  {
    id: "r_eggs_oatcakes", meal: "snack", name: "Boiled Eggs & Oatcakes", tags: ["prep-ahead", "budget"],
    kcal: 260, protein: 18, carb: 22, fat: 12,
    ingredients: ["2 boiled eggs", "3 oatcakes", "Salt & pepper"],
    method: ["Boil eggs 7–8 min, cool and peel.", "Serve with oatcakes.", "Great grab-and-go protein snack."],
  },
  {
    id: "r_racemorning", meal: "breakfast", name: "Race-Morning Porridge", tags: ["race-day", "low-fibre"],
    kcal: 450, protein: 14, carb: 82, fat: 7,
    ingredients: ["60g quick oats", "250ml water or milk", "1 banana", "1 tbsp honey", "Pinch of salt"],
    method: ["Cook oats with liquid until smooth.", "Slice in banana, add honey and salt.", "Eat 2–3 h before the start with fluids."],
  },
  {
    id: "r_rice_pot", meal: "snack", name: "Savoury Rice & Egg Pot (long-ride fuel)", tags: ["endurance", "prep-ahead"],
    kcal: 380, protein: 16, carb: 60, fat: 8,
    ingredients: ["100g cooked white rice", "1 boiled egg, chopped", "Pinch of salt", "Soy sauce to taste"],
    method: ["Mix warm rice with chopped egg.", "Season with salt and a little soy.", "Portable real-food fuel for long sessions."],
  },
  {
    id: "r_bagel_eggs", meal: "breakfast", name: "Bagel, Eggs & Spinach", tags: ["quick", "high-protein"],
    kcal: 460, protein: 28, carb: 52, fat: 15,
    ingredients: ["1 wholemeal bagel", "2 eggs", "Handful spinach", "1 tsp butter", "Salt & pepper"],
    method: ["Toast the bagel.", "Scramble eggs with wilted spinach.", "Pile onto the bagel and season."],
  },
  {
    id: "r_smoothie_bowl", meal: "breakfast", name: "Berry Protein Smoothie Bowl", tags: ["veg", "quick"],
    kcal: 420, protein: 30, carb: 56, fat: 8,
    ingredients: ["1 scoop protein", "150g frozen berries", "1 banana", "150g Greek yogurt", "30g granola"],
    method: ["Blend protein, berries, banana and yogurt thick.", "Pour into a bowl.", "Top with granola."],
  },
  {
    id: "r_pancakes", meal: "breakfast", name: "Banana Oat Protein Pancakes", tags: ["veg", "prep-ahead"],
    kcal: 500, protein: 32, carb: 60, fat: 12,
    ingredients: ["1 banana", "2 eggs", "50g oats", "1 scoop protein", "Berries to top"],
    method: ["Blend banana, eggs, oats and protein.", "Cook small pancakes in a non-stick pan.", "Stack and top with berries."],
  },
  {
    id: "r_chicken_wrap", meal: "lunch", name: "Chicken & Hummus Wrap", tags: ["quick", "high-protein"],
    kcal: 540, protein: 40, carb: 52, fat: 18,
    ingredients: ["1 large tortilla", "120g cooked chicken", "2 tbsp hummus", "Salad leaves", "Grated carrot"],
    method: ["Spread hummus over the wrap.", "Add chicken, leaves and carrot.", "Roll up tightly and slice."],
  },
  {
    id: "r_burrito_bowl", meal: "lunch", name: "Bean & Rice Burrito Bowl", tags: ["vegan", "budget", "batch-friendly"],
    kcal: 600, protein: 22, carb: 92, fat: 14,
    ingredients: ["100g (dry) rice", "1 tin black beans", "100g sweetcorn", "Salsa", "1/2 avocado", "Lime"],
    method: ["Cook rice.", "Warm the beans with spices.", "Build the bowl with corn, salsa and avocado.", "Finish with lime."],
  },
  {
    id: "r_jacket_tuna", meal: "lunch", name: "Jacket Potato, Tuna & Cheese", tags: ["budget", "high-protein"],
    kcal: 560, protein: 38, carb: 66, fat: 16,
    ingredients: ["1 large baking potato", "1 tin tuna", "30g grated cheese", "1 tbsp light mayo", "Side salad"],
    method: ["Bake the potato until soft.", "Mix tuna with mayo.", "Split potato, load with tuna and cheese.", "Serve with salad."],
  },
  {
    id: "r_falafel_salad", meal: "lunch", name: "Falafel & Couscous Salad", tags: ["veg", "prep-ahead"],
    kcal: 520, protein: 20, carb: 68, fat: 18,
    ingredients: ["4 falafel", "80g (dry) couscous", "Cherry tomatoes", "Cucumber", "2 tbsp tzatziki"],
    method: ["Soak couscous in hot stock, fluff.", "Warm the falafel.", "Toss couscous with chopped veg.", "Top with falafel and tzatziki."],
  },
  {
    id: "r_soup_bread", meal: "lunch", name: "Lentil Soup & Wholegrain Roll", tags: ["vegan", "budget", "prep-ahead"],
    kcal: 450, protein: 20, carb: 70, fat: 8,
    ingredients: ["100g red lentils", "1 carrot", "1 onion", "1 tin chopped tomatoes", "Veg stock", "1 wholegrain roll"],
    method: ["Soften onion and carrot.", "Add lentils, tomatoes and stock.", "Simmer 25 min, blend if you like.", "Serve with the roll."],
  },
  {
    id: "r_turkey_meatballs", meal: "dinner", name: "Turkey Meatballs & Spaghetti", tags: ["high-protein", "batch-friendly"],
    kcal: 660, protein: 48, carb: 74, fat: 16,
    ingredients: ["150g turkey mince", "100g (dry) spaghetti", "1 jar/tin tomato sauce", "1 egg", "Herbs, garlic"],
    method: ["Mix mince with egg and herbs; roll into balls.", "Brown the meatballs, add sauce, simmer 15 min.", "Cook spaghetti.", "Combine and serve."],
  },
  {
    id: "r_prawn_noodles", meal: "dinner", name: "Prawn & Veg Rice Noodles", tags: ["quick", "high-protein"],
    kcal: 560, protein: 36, carb: 72, fat: 12,
    ingredients: ["150g prawns", "1 nest rice noodles", "200g stir-fry veg", "1 tbsp soy sauce", "1 tsp sesame oil", "Chilli, lime"],
    method: ["Soak noodles.", "Stir-fry prawns and veg.", "Add noodles, soy and sesame.", "Finish with chilli and lime."],
  },
  {
    id: "r_chilli", meal: "dinner", name: "Beef & Bean Chilli", tags: ["batch-friendly", "budget", "high-protein"],
    kcal: 640, protein: 44, carb: 68, fat: 18,
    ingredients: ["150g lean beef mince", "1 tin kidney beans", "1 tin chopped tomatoes", "100g (dry) rice", "Chilli, cumin, garlic"],
    method: ["Brown the mince.", "Add beans, tomatoes and spices; simmer 25 min.", "Cook rice.", "Serve chilli over rice."],
  },
  {
    id: "r_veg_lasagne", meal: "dinner", name: "Roasted Veg & Lentil Lasagne", tags: ["veg", "prep-ahead"],
    kcal: 600, protein: 26, carb: 72, fat: 20,
    ingredients: ["Lasagne sheets", "1 tin green lentils", "Courgette & pepper", "1 tin chopped tomatoes", "Light cheese sauce", "Cheese"],
    method: ["Roast the veg.", "Layer tomato-lentil mix, veg, sheets and sauce.", "Top with cheese.", "Bake at 190°C for 30 min."],
  },
  {
    id: "r_fish_tacos", meal: "dinner", name: "White Fish Tacos & Slaw", tags: ["quick", "high-protein"],
    kcal: 560, protein: 38, carb: 58, fat: 18,
    ingredients: ["150g white fish", "3 small tortillas", "Shredded cabbage & carrot", "Yogurt-lime dressing", "Paprika"],
    method: ["Season and pan-cook the fish.", "Mix slaw with dressing.", "Flake fish into warm tortillas.", "Top with slaw."],
  },
  {
    id: "r_sweetpot_curry", meal: "dinner", name: "Sweet Potato & Chickpea Curry", tags: ["vegan", "batch-friendly", "budget"],
    kcal: 580, protein: 20, carb: 92, fat: 14,
    ingredients: ["1 large sweet potato", "1 tin chickpeas", "1 tin chopped tomatoes", "100g (dry) rice", "Curry paste, spinach"],
    method: ["Cook rice.", "Simmer diced sweet potato with paste and tomatoes 20 min.", "Add chickpeas and spinach.", "Serve over rice."],
  },
  {
    id: "r_pb_toast", meal: "snack", name: "Peanut Butter & Banana on Toast", tags: ["quick", "budget", "veg"],
    kcal: 320, protein: 12, carb: 42, fat: 12,
    ingredients: ["1 slice wholemeal toast", "1 tbsp peanut butter", "1 banana"],
    method: ["Toast the bread.", "Spread peanut butter.", "Top with sliced banana."],
  },
  {
    id: "r_cottage_crackers", meal: "snack", name: "Cottage Cheese & Crackers", tags: ["high-protein", "quick", "veg"],
    kcal: 250, protein: 22, carb: 24, fat: 7,
    ingredients: ["150g cottage cheese", "4 wholegrain crackers", "Cherry tomatoes", "Black pepper"],
    method: ["Spoon cottage cheese onto crackers.", "Top with tomatoes and pepper."],
  },
  {
    id: "r_edamame", meal: "snack", name: "Sea-Salt Edamame", tags: ["vegan", "quick", "high-protein"],
    kcal: 190, protein: 18, carb: 14, fat: 8,
    ingredients: ["150g frozen edamame (in pods)", "Sea salt"],
    method: ["Boil or steam edamame 4–5 min.", "Drain and toss with sea salt."],
  },
  {
    id: "r_flapjack", meal: "snack", name: "Homemade Oat & Honey Flapjack", tags: ["prep-ahead", "budget", "veg"],
    kcal: 280, protein: 6, carb: 42, fat: 10,
    ingredients: ["200g oats", "80g butter", "3 tbsp honey", "50g raisins"],
    method: ["Melt butter and honey.", "Stir in oats and raisins.", "Press into a tin, bake 20 min at 180°C.", "Cool and cut into bars."],
  },
  {
    id: "r_choc_milk", meal: "snack", name: "Chocolate Milk (recovery)", tags: ["post-workout", "quick", "veg"],
    kcal: 240, protein: 14, carb: 34, fat: 5,
    ingredients: ["400ml semi-skimmed milk", "2 tsp cocoa/chocolate powder"],
    method: ["Warm or shake milk with cocoa.", "A classic, well-evidenced recovery drink after hard sessions."],
  },
];
